import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "../../utils/formatUtils";
import { components } from "../../types/api";
import OrbLoader from "../common/OrbLoader";
import { useSelector } from "react-redux";
import { selectUserDb } from "../../store/slices/session/sessionSelector";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";

interface InsertionOrder {
  id?: string;
  name?: string;
  dailyData: components["schemas"]["DailyLineItemMetrics"][];
  summary: components["schemas"]["MetricsLineItemSummary"];
}

interface InsertionOrdersTableWithActionsProps {
  insertionOrders: InsertionOrder[] | undefined;
  isLoading: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  selectedInsertionOrderId: string | undefined;
  onInsertionOrderClick: (insertionOrder: InsertionOrder) => void;
  currency?: components["schemas"]["currency"];
  format?: "DISPLAY" | "NATIVE" | "VIDEO" | "ALL";
}

const InsertionOrdersTableWithActions: React.FC<
  InsertionOrdersTableWithActionsProps
> = ({
  insertionOrders,
  isLoading,
  maxHeight = 400,
  stickyHeader = true,
  selectedInsertionOrderId,
  onInsertionOrderClick,
  currency = "EUR",
  format = "ALL",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const navigate = useNavigate();

  const handleViewClick = (
    e: React.MouseEvent,
    insertionOrder: InsertionOrder
  ) => {
    e.stopPropagation();
    if (insertionOrder.id) {
      navigate(`/metrics/io-margins/${insertionOrder.id}`, {
        state: { insertionOrderName: insertionOrder.name },
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <OrbLoader
          size="large"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  if (!insertionOrders || insertionOrders.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t("campaigns.noInsertionOrders", {
            defaultValue: "No insertion orders available for this campaign",
          })}
        </Typography>
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase" as const,
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
  };
  const cellSx = { fontSize: "0.8125rem" };

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        maxHeight: maxHeight,
        overflow: "auto",
      }}
    >
      <Table stickyHeader={stickyHeader} size="small">
        <TableHead
          sx={{
            "& th": {
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <TableRow>
            <TableCell sx={headerCellSx}>
              {t("campaigns.name", { defaultValue: "Name" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.impressions", { defaultValue: "Impressions" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.clicks", { defaultValue: "Clicks" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.ctr", { defaultValue: "CTR" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.cpm", { defaultValue: "CPM" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.cpc", { defaultValue: "CPC" })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.cost", { defaultValue: "Cost" })}
            </TableCell>
            {format === "VIDEO" && (
              <>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.vtr", { defaultValue: "VTR" })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.firstQuartile", {
                    defaultValue: "First Quartile",
                  })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.secondQuartile", {
                    defaultValue: "Second Quartile",
                  })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.thirdQuartile", {
                    defaultValue: "Third Quartile",
                  })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.totalConversions", {
                    defaultValue: "Conversions",
                  })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.uniqueUsers", { defaultValue: "Unique Users" })}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t("campaigns.completeViews", {
                    defaultValue: "Complete Views",
                  })}
                </TableCell>
              </>
            )}
            <TableCell sx={headerCellSx}>
              {t("campaigns.measurableImpressions", {
                defaultValue: "Measurable Impressions",
              })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.engagementRate", {
                defaultValue: "Engagement Rate",
              })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.interactionTime", {
                defaultValue: "Interaction Time",
              })}
            </TableCell>
            <TableCell sx={headerCellSx}>
              {t("campaigns.totalReach", { defaultValue: "Total Reach" })}
            </TableCell>
            {isAdmin && (
              <TableCell sx={headerCellSx}>
                {t("campaigns.realCost", { defaultValue: "Real Cost" })}
              </TableCell>
            )}
            <TableCell sx={{ ...headerCellSx, width: 60 }} align="center">
              {t("common.actions", { defaultValue: "Actions" })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {insertionOrders.map((insertionOrder, index) => (
            <TableRow
              key={insertionOrder.id || insertionOrder.name}
              onClick={() => onInsertionOrderClick(insertionOrder)}
              selected={selectedInsertionOrderId === insertionOrder.id}
              sx={{
                cursor: "pointer",
                backgroundColor:
                  index % 2 === 1
                    ? theme.palette.action.hover
                    : theme.palette.background.paper,
                "& td": {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.selected,
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.action.selected,
                },
                "&.Mui-selected:hover": {
                  backgroundColor: theme.palette.action.selected,
                },
                "&:last-of-type td": { borderBottom: "none" },
              }}
            >
              <TableCell
                sx={{
                  ...cellSx,
                  fontWeight:
                    selectedInsertionOrderId === insertionOrder.id ? 600 : 400,
                }}
              >
                {insertionOrder.name || "Unnamed Insertion Order"}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatNumber(insertionOrder.summary.totalImpressions)}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatNumber(insertionOrder.summary.totalClicks)}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatPercentage(insertionOrder.summary.avgCTR)}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatCurrency(insertionOrder.summary.avgCPM, currency)}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatCurrency(insertionOrder.summary.avgCPC, currency)}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatCurrency(insertionOrder.summary.totalCost, currency)}
              </TableCell>
              {format === "VIDEO" && (
                <>
                  <TableCell sx={cellSx}>
                    {formatPercentage(insertionOrder.summary.avgVTR || 0)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatNumber(
                      insertionOrder.summary.totalFirstQuartileViews || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatNumber(
                      insertionOrder.summary.totalMidpointViews || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatNumber(
                      insertionOrder.summary.totalThirdQuartileViews || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatNumber(insertionOrder.summary.totalConversions || 0)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatNumber(insertionOrder.summary.totalUniqueUsers || 0)}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {formatNumber(
                      insertionOrder.summary.totalCompleteViews || 0
                    )}
                  </TableCell>
                </>
              )}
              <TableCell sx={cellSx}>
                {formatNumber(
                  insertionOrder.summary.totalMeasurableImpressions || 0
                )}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatPercentage(
                  insertionOrder.summary.avgEngagementRate || 0
                )}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatNumber(insertionOrder.summary.avgInteractionTime || 0)}
              </TableCell>
              <TableCell sx={cellSx}>
                {formatNumber(insertionOrder.summary.totalReachCoViewed || 0)}
              </TableCell>
              {isAdmin && (
                <TableCell sx={cellSx}>
                  {insertionOrder.summary.totalRealCost !== undefined
                    ? formatCurrency(
                        insertionOrder.summary.totalRealCost,
                        currency
                      )
                    : "-"}
                </TableCell>
              )}
              <TableCell align="center" sx={cellSx}>
                <IconButton
                  size="small"
                  onClick={(e) => handleViewClick(e, insertionOrder)}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                  disabled={!insertionOrder.id}
                  aria-label={t("common.view", { defaultValue: "View" })}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InsertionOrdersTableWithActions;
